import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Theme, ThemeContext } from './ThemeContext';

export type { Theme };

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeStorage] = useLocalStorage<Theme>('theme', 'system');
    const [isDarkMode, setIsDarkMode] = useState(false);

    const applyTheme = useCallback((currentTheme: Theme) => {
        const root = document.documentElement;
        const isDarkMode =
            currentTheme === 'dark' ||
            (currentTheme === 'system' &&
                window.matchMedia('(prefers-color-scheme: dark)').matches);

        if (isDarkMode) {
            root.classList.add('dark');
            setIsDarkMode(true);
        } else {
            root.classList.remove('dark');
            setIsDarkMode(false);
        }
    }, []);

    useEffect(() => {
        applyTheme(theme);
    }, [theme, applyTheme]);

    useEffect(() => {
        if (theme === 'system') {
            const mediaQuery = window.matchMedia(
                '(prefers-color-scheme: dark)'
            );
            const handleSystemThemeChange = () => applyTheme(theme);

            mediaQuery.addEventListener('change', handleSystemThemeChange);
            return () =>
                mediaQuery.removeEventListener(
                    'change',
                    handleSystemThemeChange
                );
        }
        return () => {};
    }, [theme, applyTheme]);

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'theme') {
                if (e.newValue) {
                    try {
                        const newTheme = JSON.parse(e.newValue) as Theme;
                        setThemeStorage(newTheme);
                    } catch {
                        /* TODO Intentionally left blank */
                    }
                } else {
                    setThemeStorage('system');
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [setThemeStorage]);

    const setTheme = useCallback(
        (newTheme: Theme) => {
            setThemeStorage(newTheme);
        },
        [setThemeStorage]
    );

    return (
        <ThemeContext.Provider value={{ theme, setTheme, isDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
}
